// Messenger Channel Service for frontend-react
// Uses Conversations API for Messenger channel connection

import { CONVERSATIONS_API_URL, META_INTEGRATION_ENABLED, getConversationsAuthToken } from '@/config/env'

export interface MessengerConnectionStatus {
  connected: boolean
  page_id?: string
  metadata?: {
    page_id?: string
    page_name?: string
    page_category?: string
    has_subscribed_apps?: boolean
    webhook_subscription_fields?: string[]
    [key: string]: unknown
  }
}

export interface MessengerConnectData {
  page_id: string
  page_access_token: string
  user_access_token?: string
}

export class MessengerServiceError extends Error {
  public status?: number
  
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'MessengerServiceError'
  }
}

class MessengerServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!META_INTEGRATION_ENABLED) {
      throw new MessengerServiceError('Meta integration is disabled in this environment', 503)
    }

    const url = `${this.baseUrl}${endpoint}`
    
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Token ${token}`,  // Use Token auth for user-specific endpoints
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new MessengerServiceError(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
          response.status
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof MessengerServiceError) {
        throw error
      }
      throw new MessengerServiceError(`Network error: ${error}`, 0)
    }
  }

  /**
   * Connect Messenger channel
   */
  async connect(data: MessengerConnectData): Promise<MessengerConnectionStatus> {
    return this.request<MessengerConnectionStatus>('/api/v1/auth/messenger/connect/', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Disconnect Messenger channel
   */
  async disconnect(): Promise<void> {
    return this.request<void>('/api/v1/auth/messenger/disconnect/', {
      method: 'POST'
    })
  }

  /**
   * Get Messenger connection status
   */
  async getStatus(): Promise<MessengerConnectionStatus> {
    const response = await this.request<MessengerConnectionStatus>('/api/v1/auth/messenger/status/')
    return response
  }
}

// Export singleton instance
export const messengerService = new MessengerServiceClient()

